import _extends from "@babel/runtime/helpers/esm/extends";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React, { Children, createContext, useContext, useLayoutEffect, useRef } from 'react';
import Spring from 'tiny-spring';
import styler from 'stylefire';
import { tabbable } from 'tabbable';
import { clamp } from '@popmotion/popcorn';
import GridView from '../GridView';
import StackView from '../StackView';
import { useMeasure } from '../hooks';
import { range } from '../utils';

function getRawTrackValue(_ref) {
  var _ref$direction = _ref.direction,
      direction = _ref$direction === void 0 ? 1 : _ref$direction,
      _ref$multiplier = _ref.multiplier,
      multiplier = _ref$multiplier === void 0 ? null : _ref$multiplier,
      _ref$spacing = _ref.spacing,
      spacing = _ref$spacing === void 0 ? 0 : _ref$spacing,
      _ref$viewsToShow = _ref.viewsToShow,
      viewsToShow = _ref$viewsToShow === void 0 ? 1 : _ref$viewsToShow;
  spacing *= 8;
  var viewSize = 100 / viewsToShow * direction;
  var spacingSize = (spacing - spacing / viewsToShow) * direction;

  if (multiplier !== null) {
    viewSize *= multiplier;
    spacingSize = spacingSize * multiplier - spacing * direction * multiplier;
  }

  if (viewsToShow === 1 && spacing === 0) {
    spacingSize = 0;
  }

  return {
    viewSize: viewSize,
    spacingSize: spacingSize
  };
}

function getTrackValue(_ref2) {
  var viewSize = _ref2.viewSize,
      spacingSize = _ref2.spacingSize;
  return "calc(" + viewSize + "% - " + spacingSize + "px)";
}

function parseTrackValue(value) {
  return parseFloat(value.includes('calc') ? value.split('(')[1].split('%')[0] : value);
}

var PageViewContext = /*#__PURE__*/createContext({});

function PagerView(props) {
  var _trackProps;

  var _props$axis = props.axis,
      axis = _props$axis === void 0 ? 'horizontal' : _props$axis,
      activeViewIndex = props.activeViewIndex,
      _props$viewsToShow = props.viewsToShow,
      viewsToShow = _props$viewsToShow === void 0 ? 1 : _props$viewsToShow,
      _props$viewsToMove = props.viewsToMove,
      viewsToMove = _props$viewsToMove === void 0 ? 1 : _props$viewsToMove,
      _props$spacing = props.spacing,
      spacing = _props$spacing === void 0 ? 0 : _props$spacing,
      _props$autoFocus = props.autoFocus,
      autoFocus = _props$autoFocus === void 0 ? false : _props$autoFocus,
      children = props.children,
      restProps = _objectWithoutPropertiesLoose(props, ["axis", "activeViewIndex", "viewsToShow", "viewsToMove", "spacing", "autoFocus", "children"]);

  var isHorizontal = axis === 'horizontal';
  var childCount = Children.count(children);
  var maxActiveIndex = childCount - viewsToShow;
  var clampedIndex = clamp(0, maxActiveIndex, activeViewIndex * viewsToMove);
  var visibleIndexes = range(0, viewsToShow).map(function (index) {
    return clampedIndex + index;
  });
  var crossDimension = axis === 'horizontal' ? 'height' : 'width';
  var frameRef = useRef();
  var frameStyler = useRef();

  var frameProps = _objectSpread({
    innerRef: frameRef,
    overflow: 'hidden'
  }, restProps);

  var trackRef = useRef();
  var trackStyler = useRef();
  var trackProps = (_trackProps = {
    innerRef: trackRef,
    alignment: isHorizontal ? 'start' : 'fill',
    autoFlow: isHorizontal ? 'column' : 'row',
    spacing: spacing
  }, _trackProps[isHorizontal ? 'gridAutoColumns' : 'gridAutoRows'] = getTrackValue(getRawTrackValue({
    spacing: spacing,
    viewsToShow: viewsToShow
  })), _trackProps);

  var _useMeasure = useMeasure(),
      useMeasureRef = _useMeasure[0],
      useMeasureEffect = _useMeasure[1];

  useMeasureEffect(function (viewNodes) {
    var viewMeasurements = viewNodes.map(function (node) {
      return node.getBoundingClientRect();
    });
    var nextFrameSize = Math.max.apply(Math, visibleIndexes.map(function (index) {
      var measurements = viewMeasurements[index];
      return measurements ? measurements[crossDimension] : -1;
    }));

    if (frameStyler.current) {
      var spring = Spring(frameStyler.current.get(crossDimension), {
        stiffness: 500,
        damping: 50
      });
      spring.onUpdate(function (value) {
        return frameStyler.current.set(crossDimension, value);
      });
      spring.transitionTo(nextFrameSize);
      return function () {
        return spring.destroy();
      };
    } else {
      frameStyler.current = styler(frameRef.current);
      frameStyler.current.set(crossDimension, nextFrameSize);
      frameStyler.current.render();
    }
  }, [activeViewIndex, viewsToShow]);
  useLayoutEffect(function () {
    var trackAxis = isHorizontal ? 'x' : 'y';
    var nextTrackValue = getRawTrackValue({
      direction: -1,
      multiplier: clampedIndex,
      spacing: spacing,
      viewsToShow: viewsToShow
    });

    if (trackStyler.current) {
      var startValue = parseTrackValue(trackStyler.current.get(trackAxis));
      var spring = Spring(startValue, {
        stiffness: 500,
        damping: 50
      });
      spring.onUpdate(function (value) {
        trackStyler.current.set(trackAxis, getTrackValue({
          viewSize: value,
          spacingSize: nextTrackValue.spacingSize
        }));
      });
      spring.transitionTo(nextTrackValue.viewSize);
      return function () {
        return spring.destroy();
      };
    } else {
      trackStyler.current = styler(trackRef.current);
      trackStyler.current.set(trackAxis, getTrackValue(nextTrackValue));
      trackStyler.current.render();
    }
  }, [clampedIndex, spacing, viewsToShow]);
  var lastTabbables = useRef([]);
  useLayoutEffect(function () {
    lastTabbables.current.forEach(function (node) {
      return node.tabIndex = 0;
    });
    lastTabbables.current = [];

    for (var index = 0; index < childCount; index++) {
      var tabbables = tabbable(trackRef.current.children[index]);

      if (autoFocus && index === clampedIndex) {
        var firstTabbable = tabbables[0];

        if (firstTabbable) {
          firstTabbable.focus({
            preventScroll: true
          });
        }
      } // prevent tabbing to any outside views


      if (!visibleIndexes.includes(index)) {
        tabbables.forEach(function (node) {
          return node.tabIndex = -1;
        });
      }

      lastTabbables.current = [].concat(lastTabbables.current, tabbables);
    }
  }, [clampedIndex, viewsToShow]);
  return /*#__PURE__*/React.createElement(StackView, frameProps, /*#__PURE__*/React.createElement(GridView, trackProps, /*#__PURE__*/React.createElement(PageViewContext.Provider, {
    value: useMeasureRef
  }, children)));
}

PagerView.View = function (props) {
  var useMeasureRef = useContext(PageViewContext);
  var viewRef = useMeasureRef();
  return /*#__PURE__*/React.createElement(StackView, _extends({
    innerRef: viewRef
  }, props));
};

export default PagerView;